import { useState, useEffect } from "react";
import { Bill } from "../types";
import { useBill } from "../context/BillContext";
import { BookOpenIcon, FileSearch, FileTextIcon } from "lucide-react";
import { useNavigate } from "react-router";

const baseUrl = "https://nigeria-tax-bill-ai-api.onrender.com";

const BillSkeleton = () => (
  <div className="bg-white rounded-xl p-6 shadow-lg">
    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
  </div>
);

const HomePage = () => {
  const [error, setError] = useState<string | null>(null);
  const [bills, setBills] = useState<Bill[]>([]);
  const [loadingBills, setLoadingBills] = useState(true);
  const { setSelectedBill } = useBill();

  const navigate = useNavigate();

  // Fetch bills on mount
  useEffect(() => {
    const fetchBills = async () => {
      try {
        const response = await fetch(`${baseUrl}/getAllBills`, {
          method: "GET",
          headers: { accept: "application/json" },
        });

        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        if (data.success) {
          setBills(data.data);
        } else {
          throw new Error(data.error || "Failed to fetch bills");
        }
      } catch (error) {
        setError(
          error instanceof Error ? error.message : "Failed to fetch bills"
        );
      } finally {
        setLoadingBills(false);
      }
    };

    fetchBills();
  }, []);

  if (loadingBills) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-purple-50 flex items-center justify-center py-12">
        <div className="max-w-4xl w-full px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Loading Bills
            </h1>
            <p className="text-gray-600 text-lg flex items-center justify-center">
              <FileSearch className="mr-2 text-green-500" />
              Retrieving Nigerian tax bills...
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((_, index) => (
              <BillSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100 px-4">
        <div className="max-w-md text-center p-8 bg-white rounded-xl shadow-2xl">
          <FileTextIcon className="mx-auto mb-4 text-red-500" size={48} />
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Oops! Bill Retrieval Failed
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-purple-50 flex items-center justify-center py-12 md:mt-0">
      <div className="max-w-4xl w-full px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center">
            <BookOpenIcon className="mr-3 text-green-800 h-10 w-10" />
            Select a Bill
          </h1>
          <p className="text-gray-600 text-lg">
            Choose a bill to start your conversation
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {bills.map((bill) => (
            <div
              key={bill.fileName}
              onClick={() => {
                setSelectedBill(bill);
                navigate(`/chat?bill=${encodeURIComponent(bill.fileName)}`);
              }}
              className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-[1.02] group"
            >
              <h3 className="text-2xl font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition">
                {bill.displayName}
              </h3>
              <p className="text-gray-600 group-hover:text-gray-800 transition">
                {bill.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
